import * as React from "react";

import {
  GenericFormConsumerInterface,
  GenericReactProviderFormInterfaceOutput,
  PRESENTATION_SCROLL_ACTIONS,
  useForm,
  usePresenterForScroll_asController,
  useProminentError,
} from "../../../Common";

import { ContentRoutes, useContentRoute } from "../content-route-provider";

import { useReactForgotPasswordInitialState } from "../react-forgot-password-initial-state-provider";

import {
  ForgotPasswordForm,
  ForgotPasswordFormApp,
  ForgotPasswordFormInitialState,
  ForgotPasswordFormLogic,
} from "./forgot-password-form";

interface ForgotPasswordFormConsumerInterface
  extends GenericFormConsumerInterface<ForgotPasswordForm> {
  resetPassword: () => void;

  isLoading: boolean;
}

const defaultValue = {} as unknown as ForgotPasswordFormConsumerInterface;

const ForgotPasswordFormContext = React.createContext(defaultValue);

interface ForgotPasswordPostResponseJson {
  success_message?: string;
  rendered_error_message_html?: string;
}

export const ForgotPasswordFormProvider: React.FunctionComponent = (props) => {
  const { setProminentError } = useProminentError();
  const { initiateScrollAction } = usePresenterForScroll_asController();

  const { initialState } = useReactForgotPasswordInitialState();

  const { onUserWantsToSelectContentRoute } = useContentRoute();

  const [isLoading, setIsLoading] = React.useState(false);

  const appContext = React.useMemo(
    () => ({
      initialState,
    }),
    [initialState]
  );
  const AppContext = React.useCallback(() => appContext, [appContext]);
  const thisForm = useForm(
    ForgotPasswordFormLogic,
    AppContext,
    ForgotPasswordFormInitialState
  );
  const { form: forgotPasswordForm, validateRightNowAndGiveMeAYesOrNo } =
    thisForm;

  const resetPassword = () => {
    const isValid = validateRightNowAndGiveMeAYesOrNo();
    if (isValid) {
      resetPasswordAction();
    } else {
      setProminentError(
        "Some information is missing or invalid. Check the red fields above for details."
      );
      initiateScrollAction(
        PRESENTATION_SCROLL_ACTIONS.SCROLL_TO_FORM_ERROR_FIELD
      );
    }
  };

  const resetPasswordAction = async () => {
    try {
      setIsLoading(true);
      await resetPasswordApi();
      onUserWantsToSelectContentRoute(ContentRoutes.Success);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);

      let errorMessage = "An unknown error has occurred.";
      if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }

      setProminentError(errorMessage);
    }
  };

  const resetPasswordApi = async () => {
    const formKey = initialState.form_key;

    const formData = new FormData();
    formData.append("form_key", formKey || "");
    formData.append("email", forgotPasswordForm.email || "");

    if (initialState.forgot_password_form_recaptcha.recaptchaEnabled) {
      const captchaWrapperId =
        initialState.forgot_password_form_recaptcha.recaptchaId + "-react-wrapper";
      let recaptchaInput = document.querySelector(
        ".bbnt-forgot-password-page > .content > .page-details > form > .input-container.recaptcha-input.named-recaptcha .g-recaptcha-response"
      ) as HTMLTextAreaElement;
      recaptchaInput =
        recaptchaInput ||
        document
          .querySelector("#" + captchaWrapperId)
          ?.querySelector(".g-recaptcha-response");

      if (recaptchaInput) {
        formData.append("g-recaptcha-response", recaptchaInput.value);
      }
    }

    const request = new Request("/customer/account/forgotpasswordpost/", {
      method: "POST",
      body: formData,
    });

    const forgotPasswordResponse = await fetch(request);

    const data: ForgotPasswordPostResponseJson =
      (await forgotPasswordResponse.json()) || {};

    if (data.success_message) {
      return true;
    } else {
      let errorMessage = "There was a problem resetting your password.";

      if (data.rendered_error_message_html) {
        errorMessage = data.rendered_error_message_html;
      }

      throw new Error(errorMessage);
    }

    return false;
  };

  return (
    <ForgotPasswordFormContext.Provider
      value={{
        ...GenericReactProviderFormInterfaceOutput<
          ForgotPasswordForm,
          ForgotPasswordFormApp
        >(thisForm),

        resetPassword,
        isLoading,
      }}
    >
      {props?.children}
    </ForgotPasswordFormContext.Provider>
  );
};

export const useForgotPasswordForm = () => {
  const context = React.useContext(ForgotPasswordFormContext);
  if (context === defaultValue) {
    throw new Error(
      "useForgotPasswordForm must be used within a <ForgotPasswordFormProvider>"
    );
  }
  return context;
};
